'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import useSWR from 'swr'
import { fetcher } from '@/utils/fetcher'
import Wallet from '@/utils/wallet'
import { API_URL } from '@/utils/constants'
import FeedHeader from '@/components/feed-header'
import WalletItemFeedEmptyState from './wallet-items-empty-state'
import styles from './WalletItemFeed.module.css'
import WalletStats from '../wallet-stats'
import useWalletBalance from '@/utils/hooks/useWalletBalance'
import event from '@/utils/event'
import WalletItems from './wallet-items'
import WalletCollectionRow from './wallet-collection-row'
import groupBy from '@/utils/group-by'
import { LocalEscrowsContext } from '@/components/local-escrows/context'
import { FILTER_COLLECTIONS_DEFAULT_ID } from '@/utils/constants'
import isFungible from '@/utils/is-fungible'
import CollectionDetail from '@/components/search-input/pages/collection-detail'
import type { Inscription } from '@/utils/types/inscription'
import type { Collection } from '@/utils/types/collections'

export type WalletTabs = 'show-all' | 'Listed' | 'Sold'

export type WalletFilters =
	| 'highest-inscription'
	| 'lowest-inscription'
	| 'collections'
	| 'most-recent'

type WalletCollectionGroup = Pick<Collection, 'name' | 'slug' | 'icon'> & {
	floor_price: number
	icon_inscription_id: string
	inscriptions: Inscription[]
	satoshis: number
}

const WalletItemFeed: FC<{
	className?: string
	isMobile: boolean
	filter?: string[]
	selectedCollectionFilter?: string
	selectedCollectionSlug?: string
	setSelected?: (filter: string, slug: string) => void
	nameSlugs?: string[]
	subTab?: string
}> = (props): ReactElement => {
	const { className, selectedCollectionFilter, selectedCollectionSlug, nameSlugs, subTab } = props
	const [selectedTab, setSelectedTab] = React.useState<WalletTabs>('show-all')
	const [filter, setFilter] = React.useState<WalletFilters>('collections')
	const address = Wallet.getTaprootAddress()
	const { localEscrows, setLocalEscrows } = React.useContext(LocalEscrowsContext)

	const handleOpenCommandView = (c: any) => {
		event.emit('command', {
			command: {
				featured: false,
				component: CollectionDetail,
				name: c.name,
				args: {
					tab: 'list',
					slug: c?.slug
				},
				heading: 'Wallet'
			}
		})
	}

	const {
		data: wallet,
		isLoading: isLoadingInscriptions,
		mutate: mutateInscriptions
	} = useWalletBalance()

	React.useEffect(() => {
		const handleListing = (params: any) => {
			const dedupedLocalEscrow = localEscrows?.filter((e: any) => e.inscription_id !== params?.id)
			if (params.type === 'success' && params.price) {
				const price = params.price * 1e8
				const escrow = {
					inscription_id: params?.id,
					satoshi_price: price,
					seller_address: address
				}
				setLocalEscrows((dedupedLocalEscrow || [])?.concat(escrow))
			} else if (params.type === 'cancel') {
				const escrow = {
					inscription_id: params?.id,
					cancelled: params?.cancelled
				}
				setLocalEscrows((dedupedLocalEscrow || [])?.concat(escrow))
			}
		}
		event.listen('list-item', handleListing)

		return () => {
			event.removeListener('list-item', handleListing)
		}
	}, [address, localEscrows, setLocalEscrows])

	const localInscriptions = wallet?.inscriptions?.map((e) => {
		const inscriptionMatch = localEscrows?.find(
			(localEscrow: any) => localEscrow?.inscription_id === e.id
		)
		if (inscriptionMatch?.cancelled) {
			return { ...e, escrow: null }
		} else if (inscriptionMatch) {
			return { ...e, escrow: inscriptionMatch }
		}

		return e
	})

	const inscriptions = props?.filter
		? localInscriptions.filter((e) => props?.filter?.includes((e?.collection?.slug as any)))
		: localInscriptions

	const {
		data: inscriptionsSold,
		isLoading: isLoadingInscriptionsSold,
		mutate: mutateInscriptionsSold
	} = useSWR(`${API_URL}/wallet/${address}/inscriptions/activity?status=Sold`, fetcher)

	const columns = React.useMemo(
		() =>
			selectedTab === 'Sold'
				? [
						{ label: 'Inscription' },
						{ label: 'Sale Price' },
						{ label: 'Timestamp' },
						{ label: 'Collection' }
				  ]
				: [{ label: 'Inscription' }, { label: 'For Sale' }, { label: 'Transfer' }],
		[selectedTab]
	)

	const handleChangeTab = React.useCallback((tab: WalletTabs) => {
		if (tab === 'Sold') {
			setFilter('most-recent')
		} else if (filter === 'most-recent') {
			setFilter('collections')
		}
		setSelectedTab(tab)
	}, [filter])

	const tabs = React.useMemo(
		() => [
			{
				title: 'Show all',
				selected: selectedTab === 'show-all',
				onClick: () => {
					handleChangeTab('show-all')
				}
			},
			{
				title: 'Listed',
				selected: selectedTab === 'Listed',
				onClick: () => {
					handleChangeTab('Listed')
				}
			},
			{
				title: 'Sold',
				selected: selectedTab === 'Sold',
				onClick: () => {
					handleChangeTab('Sold')
				}
			}
		],
		[selectedTab, handleChangeTab]
	)

	const filters = [
		selectedTab === 'Sold' && {
			title: 'Most recent',
			id: 'most-recent',
			onClick: () => {
				setFilter('most-recent')
			}
		},
		{
			title: 'Highest inscription',
			id: 'highest-inscription',
			onClick: () => {
				setFilter('highest-inscription')
			}
		},
		{
			title: 'Lowest inscription',
			id: 'lowest-inscription',
			onClick: () => {
				setFilter('lowest-inscription')
			}
		},
		{
			title: 'Collections',
			id: 'collections',
			onClick: () => {
				setFilter('collections')
				setSelectedTab('show-all')
			}
		}
	].filter((e) => !!e)

	const collectionFilters = React.useMemo(() => {
		var _collectionFilters = [
			{
				title: 'Filter Collections',
				id: FILTER_COLLECTIONS_DEFAULT_ID,
				slug: '',
				collection: null,
				count: 100000000000
			}
		]

		if (inscriptions && inscriptions.length > 0) {
			for (var inscription of inscriptions) {
				var distinctSlugs = _collectionFilters
					.map((object) => object.slug)
					.filter((value, index, self) => self.indexOf(value) === index)
				if (
					inscription?.collection?.name &&
					inscription?.collection?.slug &&
					!distinctSlugs.includes(inscription?.collection?.slug)
				) {
					var filterTitle = inscription?.collection?.name
					var collectionsWithSameSlug = inscriptions.filter(
						(object) => object?.collection?.slug === inscription?.collection?.slug
					)
					if (collectionsWithSameSlug && collectionsWithSameSlug?.length > 1) {
						filterTitle = filterTitle + ' (' + collectionsWithSameSlug?.length + ')'
					}
					if (!(nameSlugs as any).includes(inscription?.collection?.slug)) {
						_collectionFilters.push({
							title: filterTitle,
							id: inscription?.id,
							slug: inscription?.collection?.slug,
							collection: inscription?.collection as any,
							count: collectionsWithSameSlug?.length
						})
					}
				}
			}
		}

		return _collectionFilters.sort((a, b) => (a.count > b.count ? -1 : 1))
	}, [inscriptions, nameSlugs])

	const _setCollectionFilter = (id: string, slug: string) => {
		(props as any).setSelected(id, slug)
	}

	let empty: boolean = false
	let isLoading = false
	let data: any[] = []
	let mutate: any
	let dataSold: any[] = []

	if (
		selectedCollectionFilter &&
		selectedCollectionFilter !== FILTER_COLLECTIONS_DEFAULT_ID &&
		inscriptions?.length > 0
	) {
		empty = !isLoadingInscriptions && inscriptions?.length === 0
		isLoading = isLoadingInscriptions
		data = inscriptions.filter((i) => i?.collection?.slug === selectedCollectionSlug)
		dataSold = inscriptionsSold.filter((i: any) => i?.collection?.slug === selectedCollectionSlug)
	} else {
		data = inscriptions
		dataSold = inscriptionsSold
	}

	if (selectedTab === 'show-all') {
		empty = !isLoadingInscriptions && data?.length === 0
		isLoading = isLoadingInscriptions
		data =
			data?.length > 0
				? data?.sort((a, b) => {
						if (!!a?.escrow && !!b?.escrow) {
							return 0
						}

						if (!!a?.escrow) {
							return -1
						}

						if (!!b?.escrow) {
							return 1
						}

						return a.num - b.num
				  })
				: []

		mutate = mutateInscriptions
	} else if (selectedTab === 'Listed') {
		isLoading = isLoadingInscriptions
		mutate = mutateInscriptions
		data = data?.filter((i) => i?.escrow)
		empty = !isLoadingInscriptions && data?.length === 0
	} else if (selectedTab === 'Sold') {
		empty =
			inscriptionsSold?.error || (!isLoadingInscriptionsSold && inscriptionsSold?.length === 0)
		isLoading = isLoadingInscriptionsSold
		data = dataSold
		mutate = mutateInscriptionsSold
	}

	let collectionsData: WalletCollectionGroup[] = []

	if (subTab === 'my-inscriptions') {
		data = data.filter((x: any) => !nameSlugs?.includes(x?.collection?.slug))
	} else if (subTab === 'sns') {
		data = data.filter((x: { collection: { slug: string } }) =>
			nameSlugs?.includes(x?.collection?.slug)
		)
	} else if (subTab === 'brc20') {
		data = data.filter((x: any) => isFungible(x.collection?.slug))
	}

	if (filter === 'collections') {
		const collections = groupBy<Inscription>(
			data,
			(i) => i?.collection?.slug || 'Inscriptions'
		)

		for (const value of Object.values(collections)) {
			const collection = value[0]?.collection
			if (!collection) {
				continue
			}
			const floorPrice = collection.floor_price ?? 0
			collectionsData.push({
				...collection,
				icon: collection.icon ?? '',
				icon_inscription_id: collection.icon_inscription_id ?? '',
				floor_price: floorPrice,
				satoshis: floorPrice * value.length,
				inscriptions: value
			})
		}
		collectionsData?.sort((a, b) => {
			if (!a?.satoshis && a?.satoshis !== 0) {
				return 1
			}

			if (!b?.satoshis && b?.satoshis !== 0) {
				return -1
			}

			return b.satoshis - a.satoshis
		})
	}

	if (filter === 'highest-inscription') {
		data = data?.sort((a: any, b: any) => b?.num - a?.num)
	}

	if (filter === 'lowest-inscription') {
		data = data?.sort((a: any, b: any) => a?.num - b?.num)
	}

	if (selectedTab === 'Sold' && filter === 'most-recent') {
		data = data.sort(
			(a: any, b: any) =>
				(b?.escrow?.bought_at ? new Date(b?.escrow.bought_at) : new Date(0)).getTime() -
				(a?.escrow?.bought_at ? new Date(a?.escrow.bought_at) : new Date(0)).getTime()
		)
	}

	return (
		<div className={classNames({ [(props.className as any)]: !!className }, styles.root)}>
			<WalletStats
				className={styles.walletStats}
				itemCount={inscriptions?.length}
				isLoadingItems={isLoadingInscriptions}
				soldCount={inscriptionsSold?.length}
				isLoadingSold={isLoadingInscriptionsSold}
				setSelectedTab={handleChangeTab}
			/>
			<FeedHeader
				tabs={tabs}
				hideViewSelector={true}
				selectedOption={filter}
				options={filters as any}
				collectionFilters={collectionFilters}
				collectionFilter={selectedCollectionFilter}
				label="Inscription"
				setCollectionFilter={_setCollectionFilter}
				divider
			/>
			{empty && <WalletItemFeedEmptyState />}
			{!empty && filter !== 'collections' && (
				<WalletItems
					isLoading={isLoading}
					selectedTab={selectedTab}
					columns={columns}
					inscriptions={data}
					mutate={mutate}
					filter={filter}
				/>
			)}
			{!empty && filter == 'collections' && (
				<div className={styles.collectionsContainer}>
					{collectionsData.map((c, index) => (
						<WalletCollectionRow
							key={index}
							isLoading={isLoading}
							selectedTab={selectedTab}
							columns={columns}
							collection={c}
							mutate={mutate}
							onSelectBulk={() => handleOpenCommandView(c)}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export default WalletItemFeed
